import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { z } from "zod";
import { useAuth } from "@/hooks/use-auth";
import { useProfile } from "@/lib/account/profile";
import {
  AccountShell,
  SectionCard,
  Alert,
  GoldButton,
  inputClass,
  labelClass,
} from "@/components/account/AccountShell";

const schema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(60),
  lastName: z.string().trim().min(1, "Last name is required").max(60),
  phone: z
    .string()
    .trim()
    .max(20)
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || /^[+0-9()\-\s]+$/.test(v), "Phone contains invalid characters"),
  birthday: z.string().trim().max(10).optional().or(z.literal("")),
});

export const Route = createFileRoute("/account/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const { user } = useAuth();
  const [profile, saveProfile] = useProfile();
  const [form, setForm] = useState(profile);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(profile);
  }, [profile]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed.error.flatten().fieldErrors)) {
        if (v && v[0]) fe[k] = v[0];
      }
      setErrors(fe);
      return;
    }
    setErrors({});
    saveProfile({
      firstName: parsed.data.firstName,
      lastName: parsed.data.lastName,
      phone: parsed.data.phone ?? "",
      birthday: parsed.data.birthday ?? "",
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  return (
    <AccountShell>
      <SectionCard title="Personal Information">
        {saved && (
          <div className="mb-6">
            <Alert tone="success">Your details have been saved.</Alert>
          </div>
        )}
        <form onSubmit={onSubmit} className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className={labelClass}>
              First name
            </label>
            <input
              id="firstName"
              className={inputClass}
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            />
            {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className={labelClass}>
              Last name
            </label>
            <input
              id="lastName"
              className={inputClass}
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            />
            {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Email</label>
            <input
              readOnly
              value={user?.email ?? ""}
              className={`${inputClass} bg-black/[0.03] text-black/60`}
            />
            <p className="mt-1 text-xs text-black/50">
              Contact support to change your account email.
            </p>
          </div>
          <div>
            <label htmlFor="phone" className={labelClass}>
              Phone
            </label>
            <input
              id="phone"
              className={inputClass}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+33 1 23 45 67 89"
            />
            {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
          </div>
          <div>
            <label htmlFor="birthday" className={labelClass}>
              Birthday
            </label>
            <input
              id="birthday"
              type="date"
              className={inputClass}
              value={form.birthday}
              onChange={(e) => setForm({ ...form, birthday: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <GoldButton type="submit">Save changes</GoldButton>
          </div>
        </form>
      </SectionCard>
    </AccountShell>
  );
}
